<script lang="ts">
  // components
  import VintagePicker from "@shared/svelte/Components/VintagePicker.svelte";
  import ChatWithSupportLink from "@shared/svelte/Components/ChatWithSupportLink.svelte";
  import Authorization from "@shared/svelte/Components/Authorization.svelte";
  import ContainerChoice from "@retailer/svelte/ProductDetails/Components/ContainerChoice.svelte";
  import ProductBox from "@retailer/svelte/ProductDetails/Components/ProductBox.svelte";
  import DistributorChoice from "@retailer/svelte/ProductDetails/Components/DistributorChoice.svelte";
  import AddProductModal from "@retailer/svelte/ProductLines/Index/AddProductModal.svelte";
  import NewDistributorChoice from "../Components/NewDistributorChoice.svelte";
  import FulfillmentMethodBanner from "@retailer/svelte/Shared/FulfillmentMethodBanner.svelte";
  import FulfillmentMethodPicker from "@retailer/svelte/Shared/FulfillmentMethodPicker.svelte";
  import { getControlStateAuthenticationService } from "@retailer/services/retailer_user_authentications_service";

  // services
  import { buildVintagesList, hasVintage } from "@retailer/services/vintages_service";
  import {
    saveCustomPrice,
    deleteCustomPriceApi,
  } from "@retailer/svelte/ProductDetails/Api.svelte";
  import Flash from "@shared/services/flashv2.js";
  import { restrictRepAccess } from "@retailer/services/preview_forbidden_flash";
  import {
    shouldUseRetailerFulfillmentMethodForDistributor,
    hideFulfillmentMethodPickerForDistributor,
  } from "@retailer/services/fulfillment_methods_service";
  import { stagedRolloutCheck } from "@shared/svelte/Modules/StagedRolloutCheck.svelte";
  import { isAccountNumberRequired } from "@retailer/services/product_card_service";

  // stores
  import layoutStore from "@shared/svelte/Stores/LayoutStore";

  let authService = getControlStateAuthenticationService();

  // types
  import type { Distributor } from "@shared/types/Distributor";
  import type { ProductLine, Product } from "@shared/types/ProductLine";

  // props
  export let productLine: ProductLine;
  export let products: Product[];
  export let distributor: Distributor;
  export let callbacks: any;
  export let selectedVintage: string;
  export let selectedContainerType: string;
  export let productsByContainer: { [key: string]: Product[] };
  export let showDeals: boolean;
  export let showRecentlyOrdered: boolean;
  export let containerTypes: string[];
  export let Cart;
  export let alwaysChooseForceDistributorSelection: boolean;
  export let distributors: Distributor[];
  export let showDualProductExperience: boolean;
  export let selectDistributor: (distributor: Distributor) => void;
  export let pricingDataReady: boolean = false;
  export let hideAllDistributorInfo: boolean = false;
  export let retailerUserAuthStatus: string = null;

  let customPricingMode: boolean = false;
  let hasProducts: boolean;
  let priceSubmitPending: boolean = false;
  let priceEditUnit: { [key: number]: number } = {};
  let priceEditCase: { [key: number]: number } = {};
  let disableCustomPricingSubmit: boolean = true;
  let disableCustomPricingCancel: boolean = false;
  let showDistributorChoice: boolean;
  let hasValidDistributorChoices: boolean;
  let showAddProductModal = false;
  let showCustomPricing: boolean;
  let showFulfillmentMethodsDrawer: boolean = false;
  let hasPriceEdits: boolean;
  let vintages: string[] = [];
  let hasOtherDistributorChoices: boolean = false;
  let fulfillmentMethodPickerSku: string = null;

  const startCustomPricing = () => {
    if (restrictRepAccess()) {
      return;
    }
    customPricingMode = true;
    priceEditCase = {};
    priceEditUnit = {};
  };

  const cancelCustomPricing = () => {
    customPricingMode = false;
  };

  const submitCustomPricing = () => {
    if (!distributor?.id) {
      return;
    }
    let submitting = 0;
    productsByContainer[selectedContainerType].forEach((product: Product) => {
      let data: { custom_price: any; distributor_id: number } = {
        custom_price: {},
        distributor_id: distributor.id,
      };
      if (priceEditUnit[product.id] != null && !isNaN(priceEditUnit[product.id]))
        data.custom_price.unit_price = priceEditUnit[product.id];
      if (priceEditCase[product.id] != null && !isNaN(priceEditCase[product.id]))
        data.custom_price.case_price = priceEditCase[product.id];
      if (data.custom_price.unit_price || data.custom_price.case_price) {
        submitting += 1;
        if (submitting > 0) {
          priceSubmitPending = true;
        }
        saveCustomPrice(
          productLine,
          product,
          data,
          () => {
            submitting -= 1;
            delete priceEditCase[product.id];
            delete priceEditUnit[product.id];

            if (submitting === 0) {
              Flash.now({ message: "Updated custom pricing!", type: "success" });
              priceSubmitPending = false;
              customPricingMode = false;
              productsByContainer[selectedContainerType] =
                productsByContainer[selectedContainerType];
            }
          },
          () => {
            priceSubmitPending = false;
          },
        );
      }
    });
  };

  const deleteCustomPrice = product => {
    if (!confirm("Are you sure you want to delete this custom pricing information?")) return;
    deleteCustomPriceApi(product, () => {
      delete priceEditUnit[product.id];
      delete priceEditCase[product.id];
      productsByContainer[selectedContainerType] = productsByContainer[selectedContainerType];
    });
  };

  const addMissingProductSize = function () {
    if (restrictRepAccess()) {
      return;
    }
    showAddProductModal = true;
  };

  function openFulfillmentMethodsDrawer(e: CustomEvent<{ sku: string | null }>): void {
    fulfillmentMethodPickerSku = e.detail.sku;
    showFulfillmentMethodsDrawer = true;
  }

  const type = callbacks.categorization.types.find(x => {
    return x.id === productLine.product_type_id;
  });

  $: {
    showCustomPricing =
      distributor &&
      !distributor.integrated &&
      (!distributor.seven_fifty_integration || !distributor.uses_price_zones) &&
      !customPricingMode;
  }

  $: {
    // Exit custom pricing mode if the distributor changes and they don't support custom pricing
    if (
      customPricingMode &&
      (!distributor ||
        distributor.integrated ||
        (distributor.seven_fifty_integration && distributor.uses_price_zones))
    ) {
      cancelCustomPricing();
    }
  }

  $: {
    hasPriceEdits =
      !!Object.entries(priceEditUnit || {}).length || !!Object.entries(priceEditCase || {}).length;
  }
  $: vintages = buildVintagesList(products);

  $: isProductUnavailable =
    (!distributor || !hasProducts) && (!showDistributorChoice || !hasOtherDistributorChoices);
  $: canSelectVintage = hasVintage(type.name, vintages);
  $: hasSelectedVintage = !canSelectVintage || selectedVintage;
  $: hasSelectedDistributor = !!distributor?.id;
  $: displayNewDistributorChoice = showDualProductExperience && distributors.length > 1;
  $: hasOtherDistributorChoices = distributors.some(d => d.id !== distributor?.id);

  $: {
    priceEditUnit;
    priceEditCase;
    disableCustomPricingSubmit = priceSubmitPending || !hasPriceEdits;
    disableCustomPricingCancel = priceSubmitPending;
    hasProducts = products.length > 0;
    hasValidDistributorChoices =
      distributors.length > 0 &&
      (showDualProductExperience || alwaysChooseForceDistributorSelection || hasProducts);
    showDistributorChoice =
      productLine.product_type_name === "Non-Alcoholic" ||
      productLine.product_type_name === "Non-Beverage" ||
      distributor?.always_choose_distributor ||
      distributor?.state === "GA" ||
      (!hasSelectedDistributor && hasValidDistributorChoices) ||
      productLine.retailer_orders_from_class_b;
  }

  let productsForDisplay = [];
  let skus = [];

  $: {
    if (selectedContainerType) {
      productsForDisplay = productsByContainer[selectedContainerType] || [];
    } else {
      productsForDisplay = [];
    }
  }

  const wineCellarStagedRolloutEnabled = stagedRolloutCheck(
    "marketplace__wc_for_geolocation_and_account_based_distributors",
  );

  const needsAccountNumber = async () => {
    if (!wineCellarStagedRolloutEnabled)
      return (
        distributor?.requires_account_number &&
        !distributor?.is_rolling_out_integrated_orders &&
        !distributor?.account_number
      );

    const sku = productsForDisplay[0]?.sku;
    const response = await isAccountNumberRequired(
      distributor?.id,
      sku,
      null,
      distributor?.account_number,
    );
    return response;
  };

  $: usesFulfillmentMethodBanner =
    shouldUseRetailerFulfillmentMethodForDistributor(
      distributor?.id,
      distributor?.uses_warehouse_pickup,
      stagedRolloutCheck("marketplace__fulfillment_methods") && distributor?.uses_wine_cellar,
    ) && !hideFulfillmentMethodPickerForDistributor(distributor?.id);
  $: skus = products.map(p => p.sku);
</script>

<style>
  .no-gutter {
    padding-left: 0;
    padding-right: 0;
  }
</style>

<div
  class="fizz-align-self-start grid-col c4-10--lg no-gutter"
  class:fizz-stack-24={$layoutStore.isMobile}
  class:margin-top--small={$layoutStore.isDesktop}
>
  {#if hideAllDistributorInfo && !$layoutStore.isMobile && authService?.ctaComponent}
    {#if retailerUserAuthStatus == "pending"}
      <div
        class="fizz-radius-large fizz-inset-square-32 fizz-stack-16"
        style="background-color: rgb(238, 248, 246)"
      >
        <svelte:component
          this={authService?.ctaComponent}
          authStatus={retailerUserAuthStatus}
          {authService}
        />
      </div>
    {:else if retailerUserAuthStatus == "invalid"}
      <svelte:component
        this={authService?.bannerComponent}
        authStatus={retailerUserAuthStatus}
        {authService}
      />
    {/if}
  {:else if isProductUnavailable}
    <div class="fizz-inset-squish-24 fizz-text-strong fizz-text-large fizz-bordered">
      This product is currently unavailable. Please
      <ChatWithSupportLink message={"chat with support"} brandprimary={true} /> for help.
    </div>
  {:else if !hideAllDistributorInfo}
    {#if displayNewDistributorChoice}
      <NewDistributorChoice
        {distributors}
        {selectDistributor}
        {productLine}
        selectedDistributor={distributor}
      />
    {/if}
    {#if distributor?.ordering_disabled}
      <div class="fizz-alert-warning fizz-alert--small fizz-stack-12">
        Provi is temporarily not accepting orders for {distributor.name}, but you can still add
        items to your cart to order later. Contact your rep for more information.
      </div>
    {/if}
    {#if hasSelectedDistributor && canSelectVintage}
      <div class="fizz-stack-16">
        <VintagePicker {selectedVintage} {vintages} selectVintage={callbacks.selectVintage} />
      </div>
    {/if}
    {#if (hasSelectedDistributor && hasProducts) || !displayNewDistributorChoice}
      <div class="product-detail__options-border">
        <div class="product-detail__content">
          {#if showDistributorChoice && hasOtherDistributorChoices && !displayNewDistributorChoice}
            <DistributorChoice
              {distributors}
              {hasSelectedDistributor}
              {alwaysChooseForceDistributorSelection}
              {hasProducts}
              bind:distributor
              bind:productLine
              selectedDistributor={distributor}
            />
          {/if}
          {#if hasSelectedDistributor && hasSelectedVintage}
            <div>
              <ContainerChoice
                {showDeals}
                selectContainerType={callbacks.selectContainerType}
                {selectedContainerType}
                {showRecentlyOrdered}
                {containerTypes}
                isPricingLoading={!pricingDataReady}
              />
              {#if usesFulfillmentMethodBanner}
                {#await needsAccountNumber() then needsAccountNumber}
                  {#if !needsAccountNumber}
                    <div class="px-16 py-24">
                      <FulfillmentMethodBanner
                        {distributor}
                        products={productsForDisplay}
                        on:openDrawer={openFulfillmentMethodsDrawer}
                      />
                    </div>
                    <FulfillmentMethodPicker
                      bind:open={showFulfillmentMethodsDrawer}
                      {distributor}
                      products={productsForDisplay}
                      skus={fulfillmentMethodPickerSku ? [fulfillmentMethodPickerSku] : skus}
                      forProductLine={fulfillmentMethodPickerSku ? false : true}
                    />
                  {/if}
                {/await}
              {/if}
              {#each productsForDisplay as product, index (product.inventory_id)}
                <ProductBox
                  isPricingLoading={!pricingDataReady}
                  {product}
                  {callbacks}
                  {distributor}
                  {customPricingMode}
                  {productLine}
                  {Cart}
                  {deleteCustomPrice}
                  on:openDrawer={openFulfillmentMethodsDrawer}
                  bind:priceEditUnit
                  bind:priceEditCase
                  {priceSubmitPending}
                />
              {/each}

              {#if showCustomPricing}
                <Authorization authName="custom_prices.create">
                  <div class="align-center padding-top--small padding-bottom--small">
                    {#if !customPricingMode}
                      <a
                        on:click|preventDefault={startCustomPricing}
                        class="cursor-pointer brand-body--regular custom-price-button inherit-color"
                        href={"#"}
                      >
                        Edit Custom Prices
                      </a>
                    {/if}
                    <span
                      class="padding-left--smaller padding-right--smaller default-subheader--regular"
                      >|</span
                    >
                    <a
                      on:click|preventDefault={addMissingProductSize}
                      class="cursor-pointer brand-body--regular inherit-color"
                      href={"#"}
                    >
                      Add Missing Product Size
                    </a>
                    {#if customPricingMode}
                      <div id="top-custom-pricing-buttons">
                        <button
                          class="button button--label button--secondary"
                          disabled={disableCustomPricingCancel}
                          on:click={cancelCustomPricing}>Cancel</button
                        >
                        <button
                          class="button button--label button--primary"
                          disabled={disableCustomPricingSubmit}
                          on:click={submitCustomPricing}>Save</button
                        >
                      </div>
                    {/if}
                  </div>
                </Authorization>
              {/if}

              {#if customPricingMode}
                <div
                  id="bottom-custom-pricing-buttons"
                  data-testid="bottom-custom-pricing-buttons"
                  class="align-center padding-top--small padding-bottom--small"
                >
                  <button
                    class="button button--label button--secondary"
                    disabled={disableCustomPricingCancel}
                    on:click={cancelCustomPricing}>Cancel</button
                  >
                  <button
                    class="button button--label button--primary"
                    disabled={disableCustomPricingSubmit}
                    on:click={submitCustomPricing}>Save</button
                  >
                </div>
              {/if}
            </div>
          {/if}
        </div>
      </div>
    {/if}
  {/if}
</div>

<AddProductModal productLineId={productLine.id} bind:showModal={showAddProductModal} />
